import { Controller } from "@hotwired/stimulus"

// Handles the return/exchange modal workflow.
export default class extends Controller {
  static targets = [
    "reason",
    "quantity",
    "submit",
    "sizeSelect",
    "actionSelect",
    "summary",
    "stepOne",
    "stepTwo",
    "termsCheckbox",
    "nextButton",
    "spinner",
    "validationMessage"
  ]

  connect() {
    this.frameElement = this.element.closest("turbo-frame")
    this.actionSelectTargets.forEach((select) => this.updateSizeSelect(select))
    this.quantityTargets.forEach((input) => this.enforceQuantityBounds(input))
    this.updateSummary()
    this.showStepOne()
  }

  onQuantityChange(event) {
    this.enforceQuantityBounds(event.target)
    this.updateSummary()
  }

  onActionChange(event) {
    this.updateSizeSelect(event.target)
    this.updateSummary()
  }

  onReasonInput() {
    this.updateSummary()
  }

  onSizeChange() {
    this.updateSummary()
  }

  goToTerms() {
    if (!this.canProceed()) return

    this.hideValidationMessage()
    this.showStepTwo()
  }

  previousStep() {
    this.showStepOne()
  }

  onTermsToggle() {
    this.updateSubmitState()
  }

  handleSubmitStart() {
    if (this.hasSubmitTarget) {
      console.log('form submitted ---------------------->');
      this.submitTarget.disabled = true
      this.submitTarget.dataset.loading = "true"
    }
    this.showSpinner()
  }

  handleSubmitEnd() {
    if (this.hasSubmitTarget) {
      this.submitTarget.dataset.loading = "false"
    }
    this.updateSummary()
    this.hideSpinner()
  }

  clearFrame(event) {
    if (event) event.preventDefault()
    this.closeFrameAfterDelay()
  }

  // Ensures quantity stays within 0..max bounds.
  enforceQuantityBounds(input) {
    if (!input) return

    const min = parseInt(input.min, 10)
    const max = parseInt(input.max, 10)
    let value = parseInt(input.value, 10)

    if (Number.isNaN(value)) value = 0
    if (!Number.isNaN(min) && value < min) value = min
    if (!Number.isNaN(max) && value > max) value = max

    input.value = value
  }

  updateSizeSelect(select) {
    if (!select) return

    const container = select.closest("[data-order-return-target='item']")
    const sizeSelect = container?.querySelector("[data-order-return-target='sizeSelect']")
    if (!sizeSelect) return

    const hasSizes = sizeSelect.dataset.orderReturnHasSizesValue === "true"
    const isExchange = select.value === "exchange"
    const shouldEnable = isExchange && hasSizes

    sizeSelect.disabled = !shouldEnable
    sizeSelect.required = shouldEnable

    if (!shouldEnable) {
      sizeSelect.value = ""
    }
  }

  updateSummary() {
    const totalQuantity = this.quantityTargets.reduce((sum, input) => {
      const value = parseInt(input.value, 10)
      return value > 0 ? sum + value : sum
    }, 0)

    const reasonValid = this.hasReasonTarget ? this.reasonTarget.value.trim().length > 2 : true
    const formValid = totalQuantity > 0 && reasonValid

    if (this.hasSummaryTarget) {
      this.summaryTarget.textContent = totalQuantity > 0
        ? `Selected ${totalQuantity} ${totalQuantity === 1 ? "item" : "items"} for return.`
        : "No items selected yet."
    }

    this.formValid = formValid
    this.updateNextButtonState()
    this.updateSubmitState()
    if (formValid) {
      this.hideValidationMessage()
    }
  }

  closeFrameAfterDelay() {
    if (!this.frameElement) return

    setTimeout(() => {
      if (this.frameElement) {
        this.frameElement.innerHTML = ""
      }
    }, 150)
  }

  showStepOne() {
    if (this.hasStepOneTarget) {
      this.stepOneTarget.classList.remove("hidden")
    }
    if (this.hasStepTwoTarget) {
      this.stepTwoTarget.classList.add("hidden")
    }
    if (this.hasTermsCheckboxTarget) {
      this.termsCheckboxTarget.checked = false
    }
    this.updateSubmitState()
  }

  showStepTwo() {
    if (this.hasStepOneTarget) {
      this.stepOneTarget.classList.add("hidden")
    }
    if (this.hasStepTwoTarget) {
      this.stepTwoTarget.classList.remove("hidden")
    }
    this.updateSubmitState()
  }

  updateNextButtonState() {
    if (!this.hasNextButtonTarget) return

    this.nextButtonTarget.disabled = !this.formValid
    this.nextButtonTarget.classList.toggle("opacity-50", !this.formValid)
    this.nextButtonTarget.classList.toggle("cursor-not-allowed", !this.formValid)
  }

  updateSubmitState() {
    if (!this.hasSubmitTarget || this.submitTarget.dataset.loading === "true") return

    const termsAccepted = this.hasTermsCheckboxTarget ? this.termsCheckboxTarget.checked : true
    const enabled = this.formValid && termsAccepted && !this.isStepOneVisible()
    this.submitTarget.disabled = !enabled
    this.submitTarget.classList.toggle("opacity-50", !enabled)
    this.submitTarget.classList.toggle("cursor-not-allowed", !enabled)
  }

  isStepOneVisible() {
    return this.hasStepOneTarget && !this.stepOneTarget.classList.contains("hidden")
  }

  canProceed() {
    if (!this.formValid) {
      this.showValidationMessage()
      return false
    }
    return true
  }

  showSpinner() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.remove("hidden")
      this.spinnerTarget.classList.add("flex")
    }
  }

  hideSpinner() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.add("hidden")
      this.spinnerTarget.classList.remove("flex")
    }
  }

  showValidationMessage() {
    if (this.hasValidationMessageTarget) {
      this.validationMessageTarget.classList.remove("hidden")
    }
  }

  hideValidationMessage() {
    if (this.hasValidationMessageTarget) {
      this.validationMessageTarget.classList.add("hidden")
    }
  }
}
